import { Button, Card } from "@/ingot";
import { useState, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { useTenantBranding } from "@/api/queries/branding";
import {
  type ManufacturabilityWarning,
  useApplyManufacturabilityRepair,
  useToggleQuoteRepair,
} from "@/api/queries/quotes";
import { OwnRiskAction, ownRiskOffered } from "@/components/dxf/OwnRiskAction";
import { RepairBeforeAfter } from "@/components/dxf/RepairBeforeAfter";
import { formatSummaryDelta } from "@/components/dxf/RepairCompare";
import { useOpenReplaceFile } from "@/components/replaceFileContext";
import { renderMessage } from "@/components/WarningsPanel";
import { isPageVisible } from "@/shell/toggleablePages";

interface Props {
  quoteSlug: string;
  lang: string;
  /**
   * The quote's ``auto_repair_available`` — a utility node the tenant has
   * PLACED in their production map accepts this file, so the repair has
   * something to run (KAN-1345 computes it from
   * ``placed_file_utility_process_types``).
   *
   * KAN-1530: without it this card offered „Opravit soubor" to every
   * stopped quote, and a freshly onboarded shop has no map node at all —
   * the button answered ``422 repair_no_longer_applicable`` on the first
   * click, every time, whatever the geometry. The configurator's own
   * repair offer (``ManufacturabilityRepairCta``) has read this field
   * since KAN-1345; this card is the second entry point that did not.
   */
  repairAvailable: boolean;
  /** KAN-2068 — the configurator's live picks: the repair fixes what the
   *  check for THIS pick found (a hole bound that scales with the picked
   *  sheet), not the bare platform minimum. */
  dimensions?: Record<string, unknown>;
  /** KAN-1650 — poptávka odchází ve stejném okně; volající před tím
   *  vypne hlídání nepotvrzeného dílu (díl zůstává — obchod ho čte). */
  onBeforeInquiry?: () => void;
  /** KAN-2073 — nálezy, které díl zastavily. Poptávka „Upravit ručně
   *  dílnou" je nese jako předvyplněný důvod — týmž textem, jaký zákazník
   *  čte ve varováních (``renderMessage``), takže žádný kód nálezu ani
   *  limitu tu není jmenovaný. */
  findings?: ManufacturabilityWarning[];
  /** KAN-2070 — the live check's findings and the picks it ran with; the
   *  „own risk" exit is offered when a finding carries that remedy. */
  ownRisk?: {
    warnings: readonly ManufacturabilityWarning[];
    material: string | null;
    dimensions: Record<string, unknown>;
  };
  /** KAN-2081 — the original upload for the before / after review the
   *  repair shows before the customer switches to the repaired copy.
   *  ``null`` / absent = only the repaired side renders. */
  originalPreviewUrl?: string | null;
  fileName?: string;
}

/** What the repair produced, held for the customer's review (KAN-2081). */
interface RepairReview {
  repairedPreviewUrl: string;
  summaryDelta: Record<string, number>;
}

/** KAN-2073 — předvyplněná zpráva poptávky: co díl zastavilo + prosba
 *  o úpravu výkresu. Prázdná, když nález neznáme. */
export function workshopInquiryMessage(
  findings: ManufacturabilityWarning[],
  t: (key: string, opts?: Record<string, unknown>) => string,
  lang: string,
): string {
  const reasons = [...new Set(findings.map((w) => renderMessage(w, t, lang)))];
  if (reasons.length === 0) return "";
  return [...reasons, t("repair.stop.workshopAsk")].join("\n");
}

/**
 * KAN-1234 — the ways out of a stopped quote, rendered right under the reason
 * (``WarningsPanel``'s blocking section). Owner decision 2026-09-14: repair the
 * file, upload another one, or send an enquiry.
 *
 * Each exit is offered only where it can go somewhere:
 *
 *   - **repair** runs the tenant's repair node (the same consent endpoint as
 *     ``ManufacturabilityRepairCta``) and switches the item to the repaired copy,
 *     which is what the manufacturability check reads from then on. Absent when
 *     the tenant has placed no node that accepts this file (``repairAvailable``);
 *     a repair that fails, or that did not lift the stop, is not offered again;
 *   - **upload another file** opens the page shell's replace flow — absent
 *     outside the shell;
 *   - **enquiry** opens the shop's contact page in the SAME window (KAN-1650;
 *     a new tab lost the customer's context). The message links the part, so
 *     the staged item must survive the way out: ``onBeforeInquiry`` disarms
 *     the leave guard instead of letting it offer "discard". Absent when the
 *     shop hides that page.
 */
export function QuoteStopActions({
  quoteSlug,
  lang,
  repairAvailable,
  dimensions,
  onBeforeInquiry,
  findings = [],
  ownRisk,
  originalPreviewUrl = null,
  fileName = "",
}: Props): JSX.Element | null {
  const { t } = useTranslation("quote");
  const applyRepair = useApplyManufacturabilityRepair(quoteSlug);
  const switchToRepaired = useToggleQuoteRepair(quoteSlug);
  const openReplace = useOpenReplaceFile();
  const branding = useTenantBranding();
  // KAN-2073 — „Upravit ručně dílnou" je volba nálezu (``remedies``,
  // KAN-2069): nabízí se, když ji aspoň jeden nález nese. Nález bez
  // ``remedies`` (starší odpověď) ji nabízí jako dřív. A jen tam, kde obchod
  // kontaktní stránku ukazuje — jinak poptávka nemá kam odejít.
  const workshopOffered =
    findings.length === 0 ||
    findings.some((w) => !w.remedies || w.remedies.some((r) => r.kind === "workshop"));
  const showInquiry =
    workshopOffered && isPageVisible(branding.data?.pages_visibility, "contact");

  const [repairState, setRepairState] = useState<"idle" | "review" | "done" | "failed">("idle");
  const [review, setReview] = useState<RepairReview | null>(null);
  const repairing = applyRepair.isPending || switchToRepaired.isPending;
  const inquiryMessage = workshopInquiryMessage(findings, t, lang);
  const inquiryHref =
    `/${lang}/contact?topic=quote&ref=${encodeURIComponent(quoteSlug)}` +
    (inquiryMessage ? `&message=${encodeURIComponent(inquiryMessage)}` : "");
  const showRepair = repairAvailable && repairState === "idle";

  // KAN-2081 (vlastník 2026-10-15) — the repair only PREPARES the repaired
  // copy; the customer compares before / after and switches to it with
  // „Použít opravený soubor". „Zrušit" keeps the original (nothing switched).
  const onRepair = () => {
    applyRepair.mutate(dimensions, {
      onSuccess: (data) => {
        setReview({
          repairedPreviewUrl: data.repaired_preview_url,
          summaryDelta: data.summary_metric_delta,
        });
        setRepairState("review");
      },
      onError: () => setRepairState("failed"),
    });
  };
  const onUseRepaired = () => {
    switchToRepaired.mutate(true, {
      onSuccess: () => setRepairState("done"),
      onError: () => setRepairState("failed"),
    });
  };
  const onCancelRepair = () => {
    setReview(null);
    setRepairState("idle");
  };

  // Every exit gone — no placed node to repair with, no page shell to replace
  // through, no contact page to ask on. „Co s tím" over an empty row would
  // name the problem and offer nothing, which is what KAN-1530 is about.
  const showOwnRisk = ownRisk !== undefined && ownRiskOffered(ownRisk.warnings);
  if (
    !showRepair &&
    !openReplace &&
    !showInquiry &&
    !showOwnRisk &&
    repairState !== "failed" &&
    repairState !== "review"
  ) {
    return null;
  }

  if (repairState === "review" && review !== null) {
    const delta = formatSummaryDelta(review.summaryDelta, t);
    return (
      <Card padded={false} data-testid="quote-stop-repair-review">
        <div className="px-4 pt-3">
          <p className="text-sm font-medium text-ink">{t("repair.stop.review.title")}</p>
          <p className="mt-0.5 text-[12px] text-ink-3">{t("repair.stop.review.body")}</p>
        </div>
        <RepairBeforeAfter
          fileName={fileName}
          originalPreviewUrl={originalPreviewUrl}
          repairedPreviewUrl={review.repairedPreviewUrl}
        />
        <div className="flex flex-col gap-2 border-t border-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="mono text-[11px] tabular-nums text-ink-3">{delta}</span>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="ghost"
              size="md"
              onClick={onCancelRepair}
              disabled={switchToRepaired.isPending}
              data-testid="quote-stop-repair-cancel"
            >
              {t("repair.stop.review.cancel")}
            </Button>
            <Button
              variant="accent"
              size="md"
              onClick={onUseRepaired}
              disabled={switchToRepaired.isPending}
              data-testid="quote-stop-repair-use"
            >
              {switchToRepaired.isPending
                ? t("repair.stop.review.using")
                : t("repair.stop.review.use")}
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card padded data-testid="quote-stop-actions">
      <p className="text-sm font-medium text-ink">{t("repair.stop.title")}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {showRepair && (
          <Button
            variant="accent"
            size="md"
            onClick={onRepair}
            disabled={repairing}
            data-testid="quote-stop-repair"
          >
            {repairing
              ? t("repair.manufacturabilityCta.applying")
              : t("repair.stop.repair")}
          </Button>
        )}
        {openReplace && (
          <Button
            variant="ghost"
            size="md"
            onClick={openReplace}
            data-testid="quote-stop-replace"
          >
            {t("repair.stop.replace")}
          </Button>
        )}
        {showInquiry && (
          // KAN-2073 — „Upravit ručně dílnou": poptávka k dílu s nálezem
          // jako předvyplněným důvodem; dílna ji vidí ve Zprávách z kontaktu.
          // KAN-1650 — ve STEJNÉM okně: zákazník nemá ztratit kontext
          // v nové záložce. Výběr na dílu už je uložený (KAN-1652), takže
          // odchodem nic nepřijde nazmar.
          <a
            href={inquiryHref}
            onClick={onBeforeInquiry}
            data-testid="quote-stop-inquiry"
            className="inline-flex h-9 items-center rounded-md border border-border-strong bg-surface px-3 text-sm text-ink hover:bg-surface-2"
          >
            {t("repair.stop.workshop")}
          </a>
        )}
        {showOwnRisk && (
          <OwnRiskAction
            quoteSlug={quoteSlug}
            lang={lang}
            warnings={ownRisk.warnings}
            material={ownRisk.material}
            dimensions={ownRisk.dimensions}
          />
        )}
      </div>
      {repairState === "failed" && (
        <p
          className="mt-2 text-[12px] text-danger"
          role="alert"
          data-testid="quote-stop-repair-failed"
        >
          {t("repair.stop.repairFailed")}
        </p>
      )}
    </Card>
  );
}
